import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../utils/axiosConfig";
import { getApiUrl, getRelativeUrl } from "../../utils/urlHelper";

const useGroupSettings = ({ id }) => {
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [groupMedia, setGroupMedia] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [initialCoverState, setInitialCoverState] = useState(null);
  const [initialWatermarkState, setInitialWatermarkState] = useState(null);

  const fetchGroup = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/groups/${id}`);
      setGroup(res.data);
      setGroupName(res.data.name || "");
      setGroupMedia(res.data.media || []);

      setInitialCoverState({
        groupId: id,
        previewUrl: res.data.coverImage
          ? getApiUrl(getRelativeUrl(res.data.coverImage))
          : "",
        scale: res.data.coverPosition?.scale || 1,
        posX: res.data.coverPosition?.posX ?? 50,
        posY: res.data.coverPosition?.posY ?? 50,
      });

      setInitialWatermarkState({
        groupId: id,
        wmEnabled: res.data.watermark?.enabled ?? true,
        wmPreviewUrl: res.data.watermark?.image
          ? getApiUrl(getRelativeUrl(res.data.watermark.image))
          : getApiUrl("/images/logo22.png"),
        wmPosition: res.data.watermark?.position || "bottom-right",
        wmPosX: res.data.watermark?.posX ?? 85,
        wmPosY: res.data.watermark?.posY ?? 85,
        wmScale: res.data.watermark?.scale ?? 20,
        wmOpacity: res.data.watermark?.opacity ?? 70,
      });
    } catch (err) {
      console.error("Failed to load group for settings:", err);
      toast.error("Failed to load group details");
      navigate(`/group/${id}`);
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  const refreshGroup = useCallback(() => {
    fetchGroup();
  }, [fetchGroup]);

  const handleSave = async (coverData) => {
    if (!coverData.groupName.trim()) {
      toast.error("Group name cannot be empty");
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", coverData.groupName.trim());
      if (coverData.selectedFile) {
        formData.append("cover", coverData.selectedFile);
      }
      formData.append("scale", coverData.scale);
      formData.append("posX", coverData.posX);
      formData.append("posY", coverData.posY);

      const res = await api.post(`/groups/${id}/cover`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("✨ Group Settings updated successfully!");
      setGroup(res.data.group);

      if (res.data.group.name) {
        setGroupName(res.data.group.name);
      }
    } catch (err) {
      console.error("Group Settings Save Error:", err);
      toast.error("Failed to update group settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveWatermark = async (watermarkData) => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("enabled", watermarkData.wmEnabled);
      formData.append("position", watermarkData.wmPosition);
      formData.append("posX", watermarkData.wmPosX);
      formData.append("posY", watermarkData.wmPosY);
      formData.append("scale", watermarkData.wmScale);
      formData.append("opacity", watermarkData.wmOpacity);
      if (watermarkData.wmFile) {
        formData.append("watermark", watermarkData.wmFile);
      }

      const res = await api.post(`/groups/${id}/watermark`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("💧 Watermark settings saved successfully!");
      setGroup(res.data.group);
    } catch (err) {
      console.error("Watermark Save Error:", err);
      toast.error("Failed to save watermark settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm("🚨 WARNING: Are you sure you want to permanently delete this entire group? This will delete all photos, settings, and cannot be undone!")) return;

    setIsSaving(true);
    try {
      await api.delete(`/groups/${id}`);
      toast.success("Group deleted successfully!");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user?.role === "admin") {
        navigate("/admin-dashboard");
      } else {
        navigate("/user-dashboard");
      }
    } catch (err) {
      console.error("Group Delete Error:", err);
      toast.error(err.response?.data?.message || "Failed to delete group");
      setIsSaving(false);
    }
  };

  return {
    group,
    groupName,
    setGroupName,
    groupMedia,
    isLoading,
    isSaving,
    initialCoverState,
    initialWatermarkState,
    handleSave,
    handleSaveWatermark,
    refreshGroup,
    handleDeleteGroup,
  };
};

export default useGroupSettings;
